'use client';

import { AnimatePresence, motion } from 'framer-motion';
import { useAtomValue, useSetAtom } from 'jotai';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslations } from 'next-intl';
import { env } from '~/env';
import LogoButton from '~/shared/assets/brand/logo-button.svg';
import TipArrow from '~/shared/assets/icons/tip-arrow.svg';
import { buyerInitialTipShownAtom, buyerScrollTipShownAtom, markBuyerTipShownAtom } from '../model/tipSession';
import type { BuyerTipKind } from '../model/types';

interface BuyerButtonProps {
    hidden: boolean;
}

export default function BuyerButton({ hidden }: BuyerButtonProps) {
    const t = useTranslations('buyer');
    const [showTip, setShowTip] = useState(false);
    const [tipType, setTipType] = useState<BuyerTipKind>(null);

    const initialShown = useAtomValue(buyerInitialTipShownAtom);
    const scrollShown = useAtomValue(buyerScrollTipShownAtom);
    const markBuyerTipShown = useSetAtom(markBuyerTipShownAtom);

    const tipTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const showTipRef = useRef(false);
    const tipTypeRef = useRef<BuyerTipKind>(null);

    const clearTip = useCallback(() => {
        if (tipTimeoutRef.current) {
            clearTimeout(tipTimeoutRef.current);
            tipTimeoutRef.current = null;
        }

        showTipRef.current = false;
        tipTypeRef.current = null;

        setShowTip(false);
        setTipType(null);
    }, []);

    const showOneTimeTip = useCallback(
        (type: Exclude<BuyerTipKind, null>, duration = 5000, opts?: { replace?: boolean }): boolean => {
            const activeType = tipTypeRef.current;
            const hasActive = !!activeType || showTipRef.current;

            if (hasActive) {
                const canReplace = opts?.replace === true && activeType === 'initial' && type === 'scroll';

                if (!canReplace) return false;
            }

            if (type === 'initial' && initialShown) return false;
            if (type === 'scroll' && scrollShown) return false;

            tipTypeRef.current = type;
            showTipRef.current = true;

            markBuyerTipShown(type);

            setTipType(type);
            setShowTip(true);

            if (tipTimeoutRef.current) clearTimeout(tipTimeoutRef.current);

            tipTimeoutRef.current = setTimeout(() => {
                showTipRef.current = false;
                tipTypeRef.current = null;

                setShowTip(false);
                setTipType(null);
                tipTimeoutRef.current = null;
            }, duration);

            return true;
        },
        [initialShown, scrollShown, markBuyerTipShown],
    );

    useEffect(() => {
        if (!hidden) return;
        clearTip();
    }, [hidden, clearTip]);

    useEffect(() => {
        if (hidden) return;
        if (initialShown) return;

        let cancelled = false;
        let retryTimer: ReturnType<typeof setTimeout> | null = null;

        const startAt = performance.now();
        const GIVE_UP_MS = 10_000;
        const RETRY_EVERY_MS = 350;

        const tryShow = () => {
            if (cancelled) return;

            const ok = showOneTimeTip('initial');
            if (ok) return;

            if (performance.now() - startAt < GIVE_UP_MS) {
                retryTimer = setTimeout(tryShow, RETRY_EVERY_MS);
            }
        };

        const firstTimer = setTimeout(tryShow, 3000);

        return () => {
            cancelled = true;
            clearTimeout(firstTimer);
            if (retryTimer) clearTimeout(retryTimer);
        };
    }, [hidden, initialShown, showOneTimeTip]);

    useEffect(() => {
        if (hidden) return;
        if (scrollShown) return;

        const getVh = () => Math.round(window.visualViewport?.height ?? window.innerHeight);

        const MIN_DELTA_PX = 1;
        const MIN_DEPTH_PX = () => getVh() * 2;
        const MIN_UP_PX = () => Math.max(240, Math.round(getVh() * 0.22));

        let lastY = window.scrollY;
        let peakY = lastY;

        let lastDir: 'up' | 'down' | null = null;
        let upStartPeak = peakY;
        let armed = peakY >= MIN_DEPTH_PX();

        let raf = 0;
        let pendingY = lastY;

        const process = () => {
            raf = 0;

            const activeTip = tipTypeRef.current;
            if (activeTip && activeTip !== 'initial') return;

            const y = pendingY;

            if (y > peakY) peakY = y;
            if (!armed && peakY >= MIN_DEPTH_PX()) armed = true;

            const delta = y - lastY;
            if (Math.abs(delta) < MIN_DELTA_PX) {
                lastY = y;
                return;
            }

            if (delta > 0) {
                lastDir = 'down';
                upStartPeak = peakY;
                lastY = y;
                return;
            }

            if (!armed) {
                lastDir = 'up';
                lastY = y;
                return;
            }

            if (lastDir !== 'up') {
                upStartPeak = peakY;
            }
            lastDir = 'up';
            lastY = y;

            const movedUp = upStartPeak - y;

            if (movedUp >= MIN_UP_PX()) {
                showOneTimeTip('scroll', 5000, { replace: true });
            }
        };

        const onScroll = () => {
            pendingY = window.scrollY;
            if (!raf) raf = requestAnimationFrame(process);
        };

        window.addEventListener('scroll', onScroll, { passive: true });

        return () => {
            window.removeEventListener('scroll', onScroll);
            if (raf) cancelAnimationFrame(raf);
        };
    }, [hidden, scrollShown, showOneTimeTip]);

    useEffect(() => clearTip, [clearTip]);

    const supportTgLink = `https://t.me/${env.NEXT_PUBLIC_TELEGRAM_SUPPORT}`;

    if (hidden) return null;

    return (
        <>
            <AnimatePresence>
                {showTip && (
                    <motion.div
                        key={`buyer-tip-${tipType ?? 'none'}`}
                        initial={{ opacity: 0, y: 40 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -30 }}
                        transition={{ duration: 0.35, ease: 'easeInOut' }}
                        className='fixed right-[calc(var(--page-inset)_+_3.5rem)] bottom-[calc(var(--page-inset)_+_3.6rem)] z-50 flex max-w-[78vw] flex-col'
                    >
                        <div className='rounded-2xl bg-neutral-300 px-4 py-3 shadow-md'>
                            {tipType === 'initial' ? (
                                <>
                                    <p className='font-semibold text-gray-900 text-sm leading-snug'>
                                        {t('initialTitle')}
                                    </p>
                                    <p className='text-gray-700 text-sm leading-snug'>
                                        {t('body1')}&nbsp;
                                        <span className='whitespace-nowrap'>{t('body2')}</span>
                                    </p>
                                </>
                            ) : (
                                <>
                                    <p className='font-semibold text-gray-900 text-sm leading-snug'>
                                        {t('scrollTitle')}
                                    </p>
                                    <p className='text-gray-700 text-sm leading-snug'>
                                        {t('body1')}&nbsp;
                                        <span className='whitespace-nowrap'>{t('body2')}</span>
                                    </p>
                                </>
                            )}
                        </div>

                        <TipArrow className='block self-end -mt-[17px] -mr-[9.8px] h-[20px] w-[24px] text-neutral-300' />
                    </motion.div>
                )}
            </AnimatePresence>

            <motion.button
                initial={{ rotate: 0 }}
                animate={{ rotate: 0 }}
                whileHover={{ scale: 1.1, rotate: 180 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => window.open(supportTgLink, '_blank')}
                className='fixed z-[100] flex h-12 w-12 items-center justify-center rounded-full bg-black shadow-lg outline-none duration-100'
                style={{
                    cursor: 'pointer',
                    bottom: 'calc(var(--page-inset) * 1.5)',
                    right: 'calc(var(--page-inset) * 1.5)',
                }}
            >
                <LogoButton className='h-[80%] w-[80%] rounded-full' />
            </motion.button>
        </>
    );
}
